import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FolderOpen, Pencil, Trash2, Check, X, Download, Clapperboard, Loader2,
  Timer, HardDrive, ShieldCheck, RefreshCw, Film, AlertTriangle, Play, Pause, Columns2,
} from "lucide-react";
import { toast } from "sonner";
import QueuePanel from "../components/QueuePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDur = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const fmtSize = (b) => (b >= 1024 * 1024 * 1024 ? `${(b / 1024 / 1024 / 1024).toFixed(2)} GB` : `${(b / 1024 / 1024).toFixed(1)} MB`);

/* Comparador lado a lado: original y procesado reproduciéndose sincronizados */
const CompareModal = ({ video, onClose }) => {
  const aRef = useRef(null);
  const bRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [vidErr, setVidErr] = useState(false);
  const dur = video.duration || 0;

  useEffect(() => {
    const iv = setInterval(() => {
      const a = aRef.current, b = bRef.current;
      if (!a || !b) return;
      setT(a.currentTime);
      if (Math.abs(a.currentTime - b.currentTime) > 0.08) b.currentTime = a.currentTime;
      if (a.ended) setPlaying(false);
    }, 300);
    return () => clearInterval(iv);
  }, []);

  const toggle = () => {
    const a = aRef.current, b = bRef.current;
    if (!a || !b) return;
    if (playing) { a.pause(); b.pause(); setPlaying(false); }
    else {
      b.currentTime = a.currentTime;
      Promise.all([a.play(), b.play()]).then(() => setPlaying(true)).catch(() => {
        toast.error("Tu navegador no pudo reproducir el vídeo.");
      });
    }
  };

  const seek = (v) => {
    const a = aRef.current, b = bRef.current;
    if (!a || !b) return;
    a.currentTime = v; b.currentTime = v; setT(v);
  };

  return (
    <div data-testid="compare-modal" className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#241E18] rounded-3xl p-5 w-full max-w-[1200px]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-white font-display text-[16px] font-extrabold inline-flex items-center gap-2">
            <Columns2 size={16} className="text-[#FFB81C]" /> Comparar · <span className="text-white/60 font-bold truncate max-w-[400px]">{video.name}</span>
          </p>
          <button data-testid="compare-close-btn" onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="relative rounded-2xl overflow-hidden bg-black">
            <video data-testid="compare-video-original" ref={aRef} src={`${API}/videos/${video.id}/file/original`} muted playsInline preload="metadata" onError={() => setVidErr(true)} className="w-full block max-h-[52vh] object-contain" />
            <span className="absolute top-2.5 left-2.5 px-2.5 py-1 rounded-full bg-white/90 text-[#241E18] text-[10px] font-extrabold uppercase tracking-wider">Original</span>
          </div>
          <div className="relative rounded-2xl overflow-hidden bg-black">
            <video data-testid="compare-video-processed" ref={bRef} src={`${API}/videos/${video.id}/file/processed`} muted playsInline preload="metadata" onError={() => setVidErr(true)} className="w-full block max-h-[52vh] object-contain" />
            <span className="absolute top-2.5 left-2.5 px-2.5 py-1 rounded-full bg-[#F26F21] text-white text-[10px] font-extrabold uppercase tracking-wider">Procesado</span>
          </div>
        </div>
        {vidErr && (
          <div data-testid="compare-video-error" className="flex items-center gap-2 mt-3 rounded-xl bg-[#3A2E22] border border-[#5C4A35] px-4 py-2.5">
            <AlertTriangle size={15} className="text-[#FFB81C] shrink-0" />
            <p className="text-[12.5px] font-bold text-[#F0E2D2]">Tu navegador no pudo reproducir el vídeo aquí. Descárgalo para verlo en tu reproductor.</p>
          </div>
        )}
        <div className="flex items-center gap-3 mt-4">
          <button
            data-testid="compare-play-btn"
            onClick={toggle}
            className="w-11 h-11 rounded-full bg-[#F26F21] text-white flex items-center justify-center hover:bg-[#D95B12] transition-colors shrink-0"
          >
            {playing ? <Pause size={17} /> : <Play size={17} className="ml-0.5" />}
          </button>
          <input
            data-testid="compare-seek"
            type="range"
            min={0}
            max={dur}
            step={0.05}
            value={Math.min(t, dur)}
            onChange={(e) => seek(parseFloat(e.target.value))}
            className="flex-1 accent-[#F26F21]"
          />
          <span className="text-white/70 text-[12px] font-bold tabular-nums shrink-0">{fmtDur(t)} / {fmtDur(dur)}</span>
        </div>
        <p className="text-white/40 text-[11px] mt-2.5">Reproducción sincronizada fotograma a fotograma (Modo Forense: misma duración, mismos frames).</p>
      </div>
    </div>
  );
};

const STATUS = {
  uploaded: { txt: "Subido", cls: "bg-[#EEF2FF] text-[#3730A3] border-[#D8DEF8]" },
  analyzing: { txt: "Analizando…", cls: "bg-[#FEF6E7] text-[#92600A] border-[#F5DFAE]" },
  analyzed: { txt: "Analizado", cls: "bg-[#FEF6E7] text-[#92600A] border-[#F5DFAE]" },
  processing: { txt: "Procesando…", cls: "bg-[#FEF6E7] text-[#92600A] border-[#F5DFAE]" },
  done: { txt: "Procesado", cls: "bg-[#EAF7EE] text-[#1E6B3A] border-[#BFE5CB]" },
  error: { txt: "Error", cls: "bg-[#FDECEA] text-[#A4271C] border-[#F5C6C0]" },
};

const timeLeft = (createdAt) => {
  if (!createdAt) return "";
  const expires = new Date(createdAt).getTime() + 24 * 3600 * 1000;
  const ms = expires - Date.now();
  if (ms <= 0) return "expirado";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
};

const MediaLibraryPage = () => {
  const [videos, setVideos] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [compareId, setCompareId] = useState(null);
  const navigate = useNavigate();

  const load = async (silent = false) => {
    try {
      const { data } = await axios.get(`${API}/videos`);
      setVideos(data.videos);
    } catch {
      if (!silent) toast.error("No se pudo cargar la mediateca.");
      setVideos([]);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 5000); // refresca estados en curso
    return () => clearInterval(iv);
  }, []);

  const rename = async (id) => {
    const name = editName.trim();
    if (!name) return;
    try {
      await axios.patch(`${API}/videos/${id}`, { name });
      setVideos((prev) => prev.map((v) => (v.id === id ? { ...v, name } : v)));
      toast.success("Nombre actualizado");
    } catch {
      toast.error("No se pudo renombrar.");
    }
    setEditingId(null);
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/videos/${id}`);
      setVideos((prev) => prev.filter((v) => v.id !== id));
      toast.success("Vídeo eliminado del servidor");
    } catch {
      toast.error("No se pudo eliminar.");
    }
    setConfirmId(null);
  };

  return (
    <main className="min-h-[calc(100vh-72px)] bg-[#FFF7F0] py-12">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FDEBDC] text-[#C2570F] text-[12px] font-extrabold uppercase tracking-wider mb-4">
              <FolderOpen size={12} /> Mediateca
            </p>
            <h1 className="font-display text-[30px] sm:text-[38px] font-extrabold text-[#241E18] leading-tight">
              Tus vídeos, sin volver a subirlos
            </h1>
            <p className="text-[14px] text-[#8A7F73] mt-2">
              Original y procesado de cada vídeo. Reábrelos en el editor, renómbralos, descárgalos o elimínalos.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load()}
              className="h-10 w-10 rounded-full border-2 border-[#F0E2D2] bg-white flex items-center justify-center text-[#57503F] hover:border-[#F26F21] transition-all"
              title="Actualizar"
            >
              <RefreshCw size={15} />
            </button>
            <Link
              to="/editor"
              className="h-10 px-5 rounded-full bg-[#F26F21] text-white text-[13.5px] font-bold inline-flex items-center gap-2 hover:bg-[#D95B12] transition-colors"
            >
              <Film size={15} /> Subir nuevo vídeo
            </Link>
          </div>
        </div>

        <QueuePanel videos={videos || []} />

        {videos === null ? (
          <div className="rounded-3xl bg-white border border-[#F0E7DD] p-16 flex items-center justify-center gap-3">
            <Loader2 size={20} className="text-[#F26F21] animate-spin" />
            <p className="text-[14px] font-bold text-[#57503F]">Cargando mediateca…</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="rounded-3xl bg-white border border-[#F0E7DD] p-16 text-center">
            <Clapperboard size={36} className="text-[#E4D5C3] mx-auto mb-4" />
            <p className="text-[16px] font-extrabold text-[#241E18]">Aún no hay vídeos</p>
            <p className="text-[13.5px] text-[#8A7F73] mt-1 mb-6">Sube tu primer vídeo desde el editor y aparecerá aquí.</p>
            <Link to="/editor" className="inline-flex h-11 px-6 rounded-full bg-[#F26F21] text-white text-[14px] font-bold items-center hover:bg-[#D95B12] transition-colors">
              Ir al editor
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {videos.map((v) => {
              const st = STATUS[v.status] || STATUS.uploaded;
              const busy = v.status === "analyzing" || v.status === "processing";
              return (
                <article key={v.id} className="rounded-3xl bg-white border border-[#F0E7DD] p-4 sm:p-5 flex flex-col lg:flex-row gap-5 lg:items-center transition-all hover:shadow-lg hover:shadow-[#F26F21]/5">
                  {/* miniatura */}
                  <button
                    onClick={() => navigate(`/editor?video=${v.id}`)}
                    className="relative w-full lg:w-[190px] h-[110px] rounded-2xl overflow-hidden shrink-0 bg-[#241E18] group"
                    title="Abrir en el editor"
                  >
                    {v.has_original ? (
                      <img src={`${API}/videos/${v.id}/frame/1?w=320`} alt={v.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <span className="flex items-center justify-center w-full h-full text-[#8A7F73]"><Film size={24} /></span>
                    )}
                    <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded-md bg-black/65 text-white text-[10.5px] font-bold">{fmtDur(v.duration)}</span>
                  </button>

                  {/* datos */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {editingId === v.id ? (
                        <span className="flex items-center gap-1.5">
                          <input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && rename(v.id)}
                            autoFocus
                            className="h-9 px-3 rounded-lg border border-[#F26F21] text-[14px] font-bold text-[#241E18] focus:outline-none w-56"
                          />
                          <button onClick={() => rename(v.id)} className="w-8 h-8 rounded-lg bg-[#2FA45A] text-white flex items-center justify-center"><Check size={14} /></button>
                          <button onClick={() => setEditingId(null)} className="w-8 h-8 rounded-lg bg-[#EFE6DC] text-[#57503F] flex items-center justify-center"><X size={14} /></button>
                        </span>
                      ) : (
                        <>
                          <h3 className="font-display text-[16.5px] font-extrabold text-[#241E18] truncate max-w-[320px]">{v.name}</h3>
                          <button
                            onClick={() => { setEditingId(v.id); setEditName(v.name); }}
                            className="w-7 h-7 rounded-lg text-[#B4A896] hover:text-[#F26F21] hover:bg-[#FFF3E9] flex items-center justify-center transition-colors"
                            title="Renombrar"
                          >
                            <Pencil size={13} />
                          </button>
                        </>
                      )}
                      <span className={`px-2.5 py-1 rounded-full border text-[11px] font-extrabold inline-flex items-center gap-1.5 ${st.cls}`}>
                        {busy && <Loader2 size={10} className="animate-spin" />}
                        {st.txt}{busy ? ` ${v.progress || 0}%` : ""}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[12.5px] text-[#8A7F73] font-semibold">
                      <span className="inline-flex items-center gap-1"><HardDrive size={12} /> Original {v.has_original ? fmtSize(v.size_original) : "—"}</span>
                      <span className="inline-flex items-center gap-1"><ShieldCheck size={12} className={v.has_processed ? "text-[#2FA45A]" : ""} /> Procesado {v.has_processed ? fmtSize(v.size_processed) : "—"}</span>
                      <span className="inline-flex items-center gap-1 text-[#C2570F]"><Timer size={12} /> Se elimina en {timeLeft(v.created_at)}</span>
                    </div>
                  </div>

                  {/* acciones */}
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <button
                      onClick={() => navigate(`/editor?video=${v.id}`)}
                      disabled={!v.has_original || busy}
                      className="h-10 px-4 rounded-full bg-[#241E18] text-white text-[12.5px] font-bold hover:bg-[#F26F21] transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                    >
                      <Clapperboard size={13} /> Abrir en editor
                    </button>
                    {v.has_original && v.has_processed && (
                      <button
                        data-testid={`compare-btn-${v.id}`}
                        onClick={() => setCompareId(v.id)}
                        className="h-10 px-4 rounded-full border-2 border-[#241E18] bg-white text-[12.5px] font-bold text-[#241E18] hover:bg-[#241E18] hover:text-white transition-colors inline-flex items-center gap-1.5"
                      >
                        <Columns2 size={13} /> Comparar
                      </button>
                    )}
                    {v.has_processed && (
                      <a
                        href={`${API}/videos/${v.id}/file/processed?download=1`}
                        className="h-10 px-4 rounded-full bg-[#F26F21] text-white text-[12.5px] font-bold hover:bg-[#D95B12] transition-colors inline-flex items-center gap-1.5"
                      >
                        <Download size={13} /> Procesado
                      </a>
                    )}
                    {v.has_original && (
                      <a
                        href={`${API}/videos/${v.id}/file/original?download=1`}
                        className="h-10 px-4 rounded-full border-2 border-[#F0E2D2] bg-white text-[12.5px] font-bold text-[#241E18] hover:border-[#F26F21] transition-colors inline-flex items-center gap-1.5"
                      >
                        <Download size={13} /> Original
                      </a>
                    )}
                    {confirmId === v.id ? (
                      <span className="flex items-center gap-1.5 rounded-full bg-[#FDECEA] border border-[#F5C6C0] px-3 h-10">
                        <AlertTriangle size={13} className="text-[#D93025]" />
                        <span className="text-[11.5px] font-bold text-[#A4271C]">¿Eliminar?</span>
                        <button onClick={() => remove(v.id)} className="text-[11.5px] font-extrabold text-[#D93025] underline">Sí</button>
                        <button onClick={() => setConfirmId(null)} className="text-[11.5px] font-bold text-[#57503F]">No</button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirmId(v.id)}
                        className="w-10 h-10 rounded-full border-2 border-[#F0E2D2] bg-white flex items-center justify-center text-[#B4A896] hover:text-[#D93025] hover:border-[#D93025] transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <p className="flex items-center justify-center gap-2 text-[12.5px] text-[#8A7F73] mt-8">
          <ShieldCheck size={14} className="text-[#F26F21]" />
          Regla de Natur: todos los archivos se eliminan automáticamente del servidor a las 24 horas.
        </p>
      </div>
      {compareId && videos && (
        <CompareModal video={videos.find((x) => x.id === compareId)} onClose={() => setCompareId(null)} />
      )}
    </main>
  );
};

export default MediaLibraryPage;
